import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { Entitlements } from '@/types/subscription';
import { logout } from './authSlice';

interface SubscriptionState {
  entitlements: Entitlements | null;
  isLoading: boolean;
  // When a refetch fails (5xx/network), we preserve the last-known
  // entitlements instead of clearing. These two fields let the UI render a
  // subtle "may be stale" banner when we haven't confirmed server truth in
  // a while.
  lastFetchedAt: number | null;
  lastFetchErrorAt: number | null;
}

const initialState: SubscriptionState = {
  entitlements: null,
  isLoading: false,
  lastFetchedAt: null,
  lastFetchErrorAt: null,
};

const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    setEntitlements(state, action: PayloadAction<Entitlements>) {
      state.entitlements = action.payload;
      state.lastFetchedAt = Date.now();
      state.lastFetchErrorAt = null;
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    markFetchError(state) {
      state.lastFetchErrorAt = Date.now();
    },
    clearEntitlements(state) {
      state.entitlements = null;
      state.isLoading = false;
      state.lastFetchedAt = null;
      state.lastFetchErrorAt = null;
    },
  },
  extraReducers: (builder) => {
    // On logout, ALWAYS reset subscription state back to initial. Prevents
    // the next user on the same browser from briefly seeing the prior
    // user's tier/limits/stripe info when they happen to pick the same
    // currentOrgId (privacy-adjacent leak — `prevOrgIdRef === currentOrgId`
    // in useEntitlements short-circuits the refetch).
    builder.addCase(logout, () => initialState);
  },
});

export const { setEntitlements, setLoading, markFetchError, clearEntitlements } =
  subscriptionSlice.actions;
export default subscriptionSlice.reducer;
